import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import EditorJS from "@editorjs/editorjs";
import Header from "@editorjs/header";
import List from "@editorjs/list";
import OriginalTable from "@editorjs/table";
import LinkTool from "@editorjs/link";
import CustomTableTool from "./CustomTableTool";
import "./EditorJSText.scss";

// Custom Header that preserves the style field in block data
class CustomHeader extends Header {
  constructor(args) {
    super(args);
    this._savedStyle = args.data?.style || '';
    this._initialText = args.data?.text || '';
    this._initialLevel = args.data?.level || 2;
  }

  render() {
    const element = super.render();
    if (element && this._savedStyle) {
      element.setAttribute('style', this._savedStyle);
    }
    return element;
  }

  async save(blockContent) {
    try {
      const data = await super.save(blockContent);
      return { ...data, style: this._savedStyle };
    } catch (e) {
      return { text: this._initialText, level: this._initialLevel, style: this._savedStyle };
    }
  }

  static get sanitize() {
    return {
      // Explicitly whitelist every tag/attribute the registered inline tools
      // can produce (bold, italic, link, marker, inlineCode, underline, and
      // the Color tool's <font> tag) instead of relying on Editor.js's
      // inline-toolbar sanitize merging, which was stripping them on save().
      text: {
        b: true,
        i: true,
        a: { href: true, target: true, rel: true },
        mark: { class: true },
        code: true,
        u: true,
        font: { color: true, style: true },
      },
      level: false,
      style: {},
    };
  }
}

import Marker from "@editorjs/marker";
import InlineCode from "@editorjs/inline-code";
import Underline from "@editorjs/underline";
import Paragraph from "@editorjs/paragraph";
import ImageTool from "@editorjs/image";
import Delimiter from "@editorjs/delimiter";
import Quote from "@editorjs/quote";
import RawHtmlTool from "./RawHtmlTool";

// Custom Paragraph that preserves the style field in block data
class CustomParagraph extends Paragraph {
  constructor(args) {
    super(args);
    this._savedStyle = args.data?.style || '';
    this._initialText = args.data?.text || '';
  }

  render() {
    const element = super.render();
    if (element && this._savedStyle) {
      element.setAttribute('style', this._savedStyle);
    }
    return element;
  }

  async save(blockContent) {
    try {
      const data = await super.save(blockContent);
      return { ...data, style: this._savedStyle };
    } catch (e) {
      return { text: this._initialText, style: this._savedStyle };
    }
  }

  static get sanitize() {
    return {
      // Explicitly whitelist every tag/attribute the registered inline tools
      // can produce (bold, italic, link, marker, inlineCode, underline, and
      // the Color tool's <font> tag) instead of relying on Editor.js's
      // inline-toolbar sanitize merging, which was stripping them on save().
      text: {
        b: true,
        i: true,
        a: { href: true, target: true, rel: true },
        mark: { class: true },
        code: true,
        u: true,
        font: { color: true, style: true },
      },
      style: {},
    };
  }
}
import AlignmentTuneTool from "editorjs-text-alignment-blocktune";

// Try to import ColorPlugin, but don't fail if it's not installed
let ColorPlugin = null;

// Custom inline link tool that doesn't auto-add http:// prefix
// Custom Image Tool with resize functionality
class ResizableImageTool {
  static get toolbox() {
    return {
      title: 'Image',
      icon: '<svg width="17" height="15" viewBox="0 0 336 276" xmlns="http://www.w3.org/2000/svg"><path d="M291 150V79c0-19-15-34-34-34H79c-19 0-34 15-34 34v42l67-44 81 72 56-29 42 30zm0 52l-43-30-56 30-81-67-66 39v23c0 19 15 34 34 34h178c17 0 31-13 34-29zM79 0h178c44 0 79 35 79 79v118c0 44-35 79-79 79H79c-44 0-79-35-79-79V79C0 35 35 0 79 0z"/></svg>'
    };
  }

  constructor({ data, api, config }) {
    this.api = api;
    this.config = config || {};
    this.data = {
      url: data.url || '',
      caption: data.caption || '',
      width: data.width || '100%',
      alignment: data.alignment || 'center'
    };
    this.wrapper = null;
    this.image = null;
  }

  render() {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('image-tool');
    this.wrapper.style.cssText = 'position: relative; margin: 10px 0;';

    if (this.data.url) {
      this._createImage();
    } else {
      this._createUploadButton();
    }

    return this.wrapper;
  }

  _createUploadButton() {
    const uploadBtn = document.createElement('div');
    uploadBtn.style.cssText = `
      border: 2px dashed #ccc;
      border-radius: 8px;
      padding: 40px;
      text-align: center;
      cursor: pointer;
      background: #fafafa;
      transition: all 0.2s;
    `;
    uploadBtn.innerHTML = `
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
        <circle cx="8.5" cy="8.5" r="1.5"></circle>
        <polyline points="21 15 16 10 5 21"></polyline>
      </svg>
      <p style="margin: 10px 0 0; color: #666;">Click to upload image or drag & drop</p>
    `;

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.style.display = 'none';

    fileInput.addEventListener('change', (e) => {
      const file = e.target.files[0];
      if (file) this._uploadImage(file);
    });

    uploadBtn.addEventListener('click', () => fileInput.click());
    uploadBtn.addEventListener('dragover', (e) => {
      e.preventDefault();
      uploadBtn.style.borderColor = '#1BACE4';
      uploadBtn.style.background = '#e3f2fd';
    });
    uploadBtn.addEventListener('dragleave', () => {
      uploadBtn.style.borderColor = '#ccc';
      uploadBtn.style.background = '#fafafa';
    });
    uploadBtn.addEventListener('drop', (e) => {
      e.preventDefault();
      uploadBtn.style.borderColor = '#ccc';
      uploadBtn.style.background = '#fafafa';
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        this._uploadImage(file);
      }
    });

    this.wrapper.appendChild(uploadBtn);
    this.wrapper.appendChild(fileInput);
  }

  _uploadImage(file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      this.data.url = e.target.result;
      this.wrapper.innerHTML = '';
      this._createImage();
    };
    reader.readAsDataURL(file);
  }

  _createImage() {
    const container = document.createElement('div');
    container.style.cssText = `
      position: relative;
      display: inline-block;
      width: ${this.data.width};
      text-align: ${this.data.alignment};
    `;

    // Image wrapper for resize handles
    const imageWrapper = document.createElement('div');
    imageWrapper.style.cssText = `
      position: relative;
      display: inline-block;
      max-width: 100%;
    `;

    this.image = document.createElement('img');
    this.image.src = this.data.url;
    this.image.style.cssText = `
      width: 100%;
      height: auto;
      display: block;
      cursor: pointer;
    `;

    // Resize handles
    const resizeHandle = document.createElement('div');
    resizeHandle.style.cssText = `
      position: absolute;
      bottom: 0;
      right: 0;
      width: 20px;
      height: 20px;
      background: #1BACE4;
      cursor: se-resize;
      border-radius: 3px 0 0 0;
      opacity: 0;
      transition: opacity 0.2s;
    `;
    resizeHandle.innerHTML = `
      <svg width="12" height="12" viewBox="0 0 24 24" fill="white" style="margin: 4px;">
        <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM22 14H20V12H22V14ZM18 18H16V16H18V18ZM14 22H12V20H14V22Z"/>
      </svg>
    `;

    imageWrapper.addEventListener('mouseenter', () => {
      resizeHandle.style.opacity = '1';
    });
    imageWrapper.addEventListener('mouseleave', () => {
      resizeHandle.style.opacity = '0';
    });

    // Resize functionality
    let isResizing = false;
    let startX, startWidth;

    resizeHandle.addEventListener('mousedown', (e) => {
      e.preventDefault();
      isResizing = true;
      startX = e.clientX;
      startWidth = imageWrapper.offsetWidth;
      document.body.style.cursor = 'se-resize';
    });

    document.addEventListener('mousemove', (e) => {
      if (!isResizing) return;
      const diff = e.clientX - startX;
      const newWidth = Math.max(100, startWidth + diff);
      const containerWidth = this.wrapper.offsetWidth;
      const percentage = Math.min(100, Math.round((newWidth / containerWidth) * 100));
      imageWrapper.style.width = `${percentage}%`;
      this.data.width = `${percentage}%`;
    });

    document.addEventListener('mouseup', () => {
      if (isResizing) {
        isResizing = false;
        document.body.style.cursor = '';
      }
    });

    // Size buttons
    const sizeControls = document.createElement('div');
    sizeControls.style.cssText = `
      display: none;
      position: absolute;
      top: -40px;
      left: 50%;
      transform: translateX(-50%);
      background: white;
      border-radius: 6px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.15);
      padding: 5px;
      z-index: 10;
    `;

    const sizes = [
      { label: '25%', value: '25%' },
      { label: '50%', value: '50%' },
      { label: '75%', value: '75%' },
      { label: '100%', value: '100%' }
    ];

    sizes.forEach(size => {
      const btn = document.createElement('button');
      btn.textContent = size.label;
      btn.style.cssText = `
        padding: 5px 10px;
        margin: 0 2px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background: ${this.data.width === size.value ? '#1BACE4' : 'white'};
        color: ${this.data.width === size.value ? 'white' : '#333'};
        cursor: pointer;
        font-size: 12px;
      `;
      btn.addEventListener('click', () => {
        this.data.width = size.value;
        imageWrapper.style.width = size.value;
        // Update button styles
        sizeControls.querySelectorAll('button').forEach(b => {
          b.style.background = 'white';
          b.style.color = '#333';
        });
        btn.style.background = '#1BACE4';
        btn.style.color = 'white';
      });
      sizeControls.appendChild(btn);
    });

    imageWrapper.addEventListener('click', () => {
      sizeControls.style.display = sizeControls.style.display === 'none' ? 'flex' : 'none';
    });

    // Hide controls when clicking outside
    document.addEventListener('click', (e) => {
      if (!imageWrapper.contains(e.target)) {
        sizeControls.style.display = 'none';
      }
    });

    imageWrapper.appendChild(this.image);
    imageWrapper.appendChild(resizeHandle);
    imageWrapper.appendChild(sizeControls);

    // Caption
    const caption = document.createElement('input');
    caption.type = 'text';
    caption.placeholder = 'Add a caption...';
    caption.value = this.data.caption;
    caption.style.cssText = `
      width: 100%;
      border: none;
      text-align: center;
      padding: 8px;
      font-size: 14px;
      color: #666;
      background: transparent;
      outline: none;
    `;
    caption.addEventListener('input', (e) => {
      this.data.caption = e.target.value;
    });

    container.appendChild(imageWrapper);
    container.appendChild(caption);
    this.wrapper.appendChild(container);
  }

  save() {
    return {
      url: this.data.url,
      caption: this.data.caption,
      width: this.data.width,
      alignment: this.data.alignment
    };
  }

  validate(savedData) {
    return savedData.url && savedData.url.trim() !== '';
  }
}

// Image Gallery Tool - Multiple images in a single row
class ImageGalleryTool {
  static get toolbox() {
    return {
      title: 'Image Gallery',
      icon: '<svg width="17" height="15" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z" fill="currentColor"/></svg>'
    };
  }

  constructor({ data, api, config }) {
    this.api = api;
    this.config = config || {};
    this.data = {
      images: data.images || [], // Array of { url, caption }
      layout: data.layout || 'auto', // 'auto', '2', '3', '4'
      gap: data.gap || '10px'
    };
    this.wrapper = null;
  }

  render() {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('image-gallery-tool');
    this.wrapper.style.cssText = 'position: relative; margin: 10px 0;';

    this._renderGallery();
    return this.wrapper;
  }

  _renderGallery() {
    this.wrapper.innerHTML = '';

    // Layout controls
    const controls = document.createElement('div');
    controls.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
      padding: 8px;
      background: #f5f5f5;
      border-radius: 6px;
    `;

    const layoutLabel = document.createElement('span');
    layoutLabel.textContent = 'Images per row:';
    layoutLabel.style.cssText = 'font-size: 12px; color: #666;';

    const layoutSelect = document.createElement('select');
    layoutSelect.style.cssText = `
      padding: 4px 8px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 12px;
      cursor: pointer;
    `;
    ['auto', '2', '3', '4'].forEach(opt => {
      const option = document.createElement('option');
      option.value = opt;
      option.textContent = opt === 'auto' ? 'Auto' : `${opt} columns`;
      option.selected = this.data.layout === opt;
      layoutSelect.appendChild(option);
    });
    layoutSelect.addEventListener('change', (e) => {
      this.data.layout = e.target.value;
      this._renderGallery();
    });

    const addBtn = document.createElement('button');
    addBtn.textContent = '+ Add Image';
    addBtn.style.cssText = `
      padding: 6px 12px;
      background: #1BACE4;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 12px;
    `;
    addBtn.addEventListener('click', () => this._addImage());

    controls.appendChild(layoutLabel);
    controls.appendChild(layoutSelect);
    controls.appendChild(addBtn);
    this.wrapper.appendChild(controls);

    // Gallery container
    const gallery = document.createElement('div');
    const columns = this.data.layout === 'auto' 
      ? Math.min(this.data.images.length || 1, 4) 
      : parseInt(this.data.layout);
    
    gallery.style.cssText = `
      display: grid;
      grid-template-columns: repeat(${columns}, 1fr);
      gap: ${this.data.gap};
    `;

    if (this.data.images.length === 0) {
      // Show upload placeholder
      const placeholder = document.createElement('div');
      placeholder.style.cssText = `
        grid-column: 1 / -1;
        border: 2px dashed #ccc;
        border-radius: 8px;
        padding: 40px;
        text-align: center;
        cursor: pointer;
        background: #fafafa;
      `;
      placeholder.innerHTML = `
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#999" stroke-width="2">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <circle cx="8.5" cy="8.5" r="1.5"></circle>
          <polyline points="21 15 16 10 5 21"></polyline>
        </svg>
        <p style="margin: 10px 0 0; color: #666;">Click to add images</p>
      `;
      placeholder.addEventListener('click', () => this._addImage());
      gallery.appendChild(placeholder);
    } else {
      this.data.images.forEach((img, index) => {
        const imageItem = this._createImageItem(img, index);
        gallery.appendChild(imageItem);
      });
    }

    this.wrapper.appendChild(gallery);
  }

  _createImageItem(imgData, index) {
    const item = document.createElement('div');
    item.style.cssText = `
      position: relative;
      border-radius: 6px;
      overflow: hidden;
      background: #f5f5f5;
    `;

    const img = document.createElement('img');
    img.src = imgData.url;
    img.style.cssText = `
      width: 100%;
      height: auto;
      display: block;
    `;

    // Delete button
    const deleteBtn = document.createElement('button');
    deleteBtn.innerHTML = '×';
    deleteBtn.style.cssText = `
      position: absolute;
      top: 5px;
      right: 5px;
      width: 24px;
      height: 24px;
      background: rgba(255,0,0,0.8);
      color: white;
      border: none;
      border-radius: 50%;
      cursor: pointer;
      font-size: 16px;
      line-height: 1;
      opacity: 0;
      transition: opacity 0.2s;
    `;
    deleteBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.data.images.splice(index, 1);
      this._renderGallery();
    });

    // Caption input
    const caption = document.createElement('input');
    caption.type = 'text';
    caption.placeholder = 'Caption...';
    caption.value = imgData.caption || '';
    caption.style.cssText = `
      width: 100%;
      border: none;
      padding: 8px;
      font-size: 12px;
      color: #666;
      background: white;
      outline: none;
      box-sizing: border-box;
    `;
    caption.addEventListener('input', (e) => {
      this.data.images[index].caption = e.target.value;
    });

    item.addEventListener('mouseenter', () => {
      deleteBtn.style.opacity = '1';
    });
    item.addEventListener('mouseleave', () => {
      deleteBtn.style.opacity = '0';
    });

    item.appendChild(img);
    item.appendChild(deleteBtn);
    item.appendChild(caption);

    return item;
  }

  _addImage() {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.multiple = true;
    fileInput.style.display = 'none';

    fileInput.addEventListener('change', (e) => {
      const files = Array.from(e.target.files);
      files.forEach(file => {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (ev) => {
            this.data.images.push({
              url: ev.target.result,
              caption: ''
            });
            this._renderGallery();
          };
          reader.readAsDataURL(file);
        }
      });
    });

    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  }

  save() {
    return {
      images: this.data.images,
      layout: this.data.layout,
      gap: this.data.gap
    };
  }

  validate(savedData) {
    return savedData.images && savedData.images.length > 0;
  }
}

class CustomLinkInlineTool {
  static get isInline() {
    return true;
  }

  static get sanitize() {
    return {
      a: {
        href: true,
        target: '_blank',
        rel: 'noopener noreferrer'
      }
    };
  }

  static get title() {
    return 'Link';
  }

  constructor({ api }) {
    this.api = api;
    this.button = null;
    this.state = false;
    this.tag = 'A';
    this.nodes = {
      input: null,
    };
  }

  render() {
    this.button = document.createElement('button');
    this.button.type = 'button';
    // Simple link icon with strokes matching other toolbar icons
    this.button.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width:14px;height:14px;max-width:14px;"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>';
    this.button.classList.add(this.api.styles.inlineToolButton);
    return this.button;
  }

  surround(range) {
    if (this.state) {
      this.unlink();
      return;
    }

    this.savedSelection = this.api.selection.save();
    this.showActions();
  }

  checkState() {
    const anchorTag = this.api.selection.findParentTag(this.tag);
    this.state = !!anchorTag;
    return this.state;
  }

  renderActions() {
    this.nodes.input = document.createElement('input');
    this.nodes.input.placeholder = 'Enter URL or variable (e.g., %url%)';
    this.nodes.input.classList.add(this.api.styles.input);
    this.nodes.input.style.cssText = 'padding: 6px; border: 1px solid #ccc; border-radius: 3px; width: 280px;';
    
    this.nodes.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.enterPressed();
      }
    });

    return this.nodes.input;
  }

  showActions() {
    this.nodes.input.value = '';
    this.nodes.input.hidden = false;
    this.nodes.input.focus();
  }

  hideActions() {
    if (this.nodes.input) {
      this.nodes.input.hidden = true;
    }
  }

  enterPressed() {
    let value = this.nodes.input.value.trim();
    
    if (!value) {
      this.hideActions();
      return;
    }

    // Check for variable syntax - don't modify these
    const hasVariableSyntax = /%[^%]+%/.test(value) || /\{\{[^}]+\}\}/.test(value) || /\{[^}]+\}/.test(value);
    const hasProtocol = /^https?:\/\//i.test(value);
    const looksLikeDomain = /^[a-zA-Z0-9][a-zA-Z0-9-]*\.[a-zA-Z]{2,}/.test(value);
    const isRelativePath = /^[a-zA-Z0-9_-]+\//.test(value); // e.g., site/view/123/general-info
    
    if (hasVariableSyntax) {
      // Keep variables as-is (e.g., %adminPlatformUrl%)
    } else if (hasProtocol) {
      // Already has protocol, keep as-is
    } else if (isRelativePath) {
      // Relative path like "site/view/123/general-info" - prepend ADMIN_URL (current origin)
      const adminUrl = window.location.origin;
      value = `${adminUrl}/${value}`;
    } else if (looksLikeDomain) {
      // Looks like a domain without protocol
      value = 'https://' + value;
    }

    this.api.selection.restore(this.savedSelection);
    
    // Use window.getSelection() instead of api.selection.getRange()
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const selectedText = range.extractContents();
      const anchor = document.createElement('a');
      anchor.href = value;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.appendChild(selectedText);
      range.insertNode(anchor);
      
      // Move cursor after the link
      range.setStartAfter(anchor);
      range.collapse(true);
      
      selection.removeAllRanges();
      selection.addRange(range);
    }

    this.hideActions();
    this.api.inlineToolbar.close();
  }

  unlink() {
    const anchorTag = this.api.selection.findParentTag(this.tag);
    if (anchorTag) {
      this.api.selection.expandToTag(anchorTag);
      const range = this.api.selection.getRange();
      const text = range.extractContents();
      anchorTag.parentNode.insertBefore(text, anchorTag);
      anchorTag.remove();
    }
  }

  clear() {
    this.hideActions();
  }
}

// Custom Color Inline Tool - built from scratch to avoid buggy npm packages
// Hesus brand palette - kept as quick-access swatches, but the custom
// RGB/Hex picker below lets users pick any color that matches their needs.
const COLOR_PALETTE = [
  '#1BACE4', '#1589B6', '#004C7F', '#1e3a5f', '#32A728', '#E83F1E',
  '#ff9800', '#ff6b35', '#333333', '#666666', '#999999', '#FFFFFF'
];

// Basic hex validation: #fff or #ffffff
const HEX_COLOR_REGEX = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

// Normalize to the 6-digit lowercase form (#rrggbb) that <input type="color">
// requires; 3-digit shorthand is expanded. Returns null when invalid.
const normalizeHexColor = (value) => {
  const trimmed = (value || '').trim();
  if (!HEX_COLOR_REGEX.test(trimmed)) return null;
  let hex = trimmed.slice(1);
  if (hex.length === 3) {
    hex = hex.split('').map((ch) => ch + ch).join('');
  }
  return '#' + hex.toLowerCase();
};


// the picker preview.
let lastSelectedColor = '#FF1300';

class CustomColorInlineTool {
  constructor({ api }) {
    this.api = api;
    this.button = null;
    this.colorPicker = null;
    this.selectedColor = lastSelectedColor;
    this._state = false;
    this.savedRange = null;
  }

  _setSelectedColor(color) {
    this.selectedColor = color;
    lastSelectedColor = color;
  }

  static get isInline() {
    return true;
  }

  static get sanitize() {
    return {
      font: {
        color: true,
        style: true
      }
    };
  }

  static get title() {
    return 'Color';
  }

  render() {
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.classList.add(this.api.styles.inlineToolButton);
    this.button.innerHTML = '<span class="color-tool-icon">A</span>';
    this._setIconColor(this.selectedColor);

    return this.button;
  }

  renderActions() {
    // All presentation lives in EditorJSText.scss (unscoped rules, because
    // Editor.js mounts the inline toolbar popover on document.body, outside
    // the .editorjs-text-wrapper subtree). Only dynamic values (the swatch
    // background colors) stay inline.
    this.colorPicker = document.createElement('div');
    this.colorPicker.classList.add('color-picker-dropdown');

    const grid = document.createElement('div');
    grid.classList.add('color-picker-grid');

    COLOR_PALETTE.forEach(color => {
      const colorBtn = document.createElement('button');
      colorBtn.type = 'button';
      colorBtn.classList.add('color-picker-swatch');
      if (color === '#FFFFFF') {
        colorBtn.classList.add('color-picker-swatch--white');
      }
      colorBtn.style.background = color;
      colorBtn.addEventListener('mousedown', (e) => {
        e.preventDefault(); // Prevent losing selection
        e.stopPropagation();
      });
      colorBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._setSelectedColor(color);
        this.applyColor(color);
      });
      grid.appendChild(colorBtn);
    });
    
    this.colorPicker.appendChild(grid);

    // Custom RGB / Hex color picker - lets the user pick any color instead
    // of being limited to the predefined swatches above.
    const customRow = document.createElement('div');
    customRow.classList.add('color-picker-custom-row');

    const nativeColorInput = document.createElement('input');
    nativeColorInput.type = 'color';
    nativeColorInput.classList.add('color-picker-native-input');
    nativeColorInput.value = normalizeHexColor(this.selectedColor) || '#1bace4';
    nativeColorInput.addEventListener('mousedown', (e) => e.stopPropagation());

    const hexInput = document.createElement('input');
    hexInput.type = 'text';
    hexInput.classList.add('color-picker-hex-input');
    hexInput.placeholder = '#RRGGBB';
    hexInput.value = nativeColorInput.value;
    hexInput.addEventListener('mousedown', (e) => e.stopPropagation());
    hexInput.addEventListener('keydown', (e) => e.stopPropagation());

    const applyBtn = document.createElement('button');
    applyBtn.type = 'button';
    applyBtn.classList.add('color-picker-apply-btn');
    applyBtn.textContent = 'Apply';

    const applyFromHexInput = () => {
      const value = normalizeHexColor(hexInput.value);
      if (value) {
        this._setSelectedColor(value);
        nativeColorInput.value = value;
        hexInput.value = value;
        this.applyColor(value);
      }
    };

    // Live preview: update the native color input as soon as the typed hex
    // is valid, without applying it to the text yet.
    hexInput.addEventListener('input', () => {
      const value = normalizeHexColor(hexInput.value);
      if (value) {
        nativeColorInput.value = value;
      }
    });

    nativeColorInput.addEventListener('input', (e) => {
      hexInput.value = e.target.value;
      this._setSelectedColor(e.target.value);
      this.applyColor(e.target.value);
    });

    hexInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        applyFromHexInput();
      }
    });

    applyBtn.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
    });
    applyBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      applyFromHexInput();
    });

    customRow.appendChild(nativeColorInput);
    customRow.appendChild(hexInput);
    customRow.appendChild(applyBtn);
    this.colorPicker.appendChild(customRow);

    return this.colorPicker;
  }

  surround(range) {
    // Save the range for later use
    this.savedRange = range.cloneRange();
    // Don't apply color here - let the color picker handle it
  }

  wrap(range) {
    const selectedText = range.extractContents();
    const font = document.createElement('FONT');
    font.style.color = this.selectedColor;
    font.appendChild(selectedText);
    range.insertNode(font);
    this.api.selection.expandToTag(font);
  }

  unwrap(range) {
    const font = this.api.selection.findParentTag('FONT');
    if (font) {
      this.api.selection.expandToTag(font);
      const text = range.extractContents();
      font.parentNode.insertBefore(text, font);
      font.parentNode.removeChild(font);
    }
  }

  checkState(selection) {
    const font = this.api.selection.findParentTag('FONT');
    this._state = !!font;
    
    // Save the current selection range
    const windowSelection = window.getSelection();
    if (windowSelection.rangeCount > 0) {
      this.savedRange = windowSelection.getRangeAt(0).cloneRange();
    }
    
    return this._state;
  }

  applyColor(color) {
    // Restore the saved selection
    if (this.savedRange) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(this.savedRange);
    }
    
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (!range.collapsed) {
        // Check if already wrapped in FONT tag
        const existingFont = this.api.selection.findParentTag('FONT');
        if (existingFont) {
          // Just update the color
          existingFont.style.color = color;
        } else {
          // Wrap in new FONT tag
          const selectedText = range.extractContents();
          const font = document.createElement('FONT');
          font.style.color = color;
          font.appendChild(selectedText);
          range.insertNode(font);
          
          // Re-select the colored text
          selection.removeAllRanges();
          const newRange = document.createRange();
          newRange.selectNodeContents(font);
          selection.addRange(newRange);
          this.savedRange = newRange.cloneRange();
        }
      }
    }
    
    // Update button indicator
    this._setIconColor(color);
  }

  _setIconColor(color) {
    const icon = this.button && this.button.querySelector('.color-tool-icon');
    if (icon) {
      icon.style.textDecorationColor = color;
    }
  }

  clear() {
    this.savedRange = null;
  }
}

ColorPlugin = CustomColorInlineTool;

const EditorJSText = forwardRef((props, ref) => {
  const { setDescriptionData, descriptionData, disabled } = props;
  const editorInstance = useRef(null);
  const holderRef = useRef(null);

  // Track the last descriptionData to detect external changes
  const lastDescriptionDataRef = useRef(descriptionData);
  
  // Flag to prevent onChange from firing during external content updates
  const isExternalUpdateRef = useRef(false);
  
  // Track the last cursor position for variable insertion
  const lastRangeRef = useRef(null);
  const isEditorFocusedRef = useRef(false);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    // Method to update editor content externally (e.g., when switching tabs)
    updateContent: async (newContent) => {
      if (!editorInstance.current) return;
      
      try {
        let newData = { blocks: [] };
        
        if (newContent) {
          try {
            const parsed = JSON.parse(newContent);
            if (parsed && parsed.blocks && Array.isArray(parsed.blocks)) {
              newData = parsed;
            }
          } catch (e) {
            // If it's HTML, convert to a simple paragraph block
            newData = {
              blocks: [
                {
                  type: "paragraph",
                  data: { text: String(newContent || '') },
                },
              ],
            };
          }
        }
        
        await editorInstance.current.clear();
        await editorInstance.current.render(newData);
        lastDescriptionDataRef.current = newContent;
      } catch (error) {
        console.error('Error updating editor content:', error);
      }
    },
    insertVariable: async (variable) => {
      if (!editorInstance.current || !holderRef.current) return;
      
      try {
        const selection = window.getSelection();
        
        // Try to restore the last saved range if we have one
        if (lastRangeRef.current) {
          try {
            const savedRange = lastRangeRef.current;
            
            // Verify the range is still valid (nodes exist in document)
            if (savedRange.startContainer && document.contains(savedRange.startContainer)) {
              // Clone the range to avoid issues
              const range = savedRange.cloneRange();
              range.collapse(false); // Collapse to end of selection
              
              selection.removeAllRanges();
              selection.addRange(range);
              
              // Insert at cursor position
              document.execCommand('insertText', false, variable);
              return;
            }
          } catch (e) {
            console.warn('Could not restore range:', e);
          }
        }
        
        // Fallback: Find the last focused paragraph or first available
        const paragraphs = holderRef.current.querySelectorAll('.ce-paragraph[contenteditable="true"]');
        const editableElement = paragraphs.length > 0 ? paragraphs[paragraphs.length - 1] : null;
        
        if (editableElement) {
          editableElement.focus();
          
          // Place cursor at the end
          const range = document.createRange();
          range.selectNodeContents(editableElement);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
          
          document.execCommand('insertText', false, variable);
        } else {
          // If no paragraph exists, create one first then insert
          await editorInstance.current.blocks.insert('paragraph', { text: '' });
          
          setTimeout(() => {
            const newEditableElement = holderRef.current.querySelector('.ce-paragraph[contenteditable="true"]');
            if (newEditableElement) {
              newEditableElement.focus();
              document.execCommand('insertText', false, variable);
            }
          }, 50);
        }
      } catch (error) {
        console.error('Error inserting variable:', error);
      }
    },
    // Method to get the editor instance for direct access
    getEditorInstance: () => editorInstance.current
  }), []);

  // Helper function to check if content is JSON (EditorJS format)
  const isJsonContent = (content) => {
    if (!content || typeof content !== 'string') return false;
    const trimmed = content.trim();
    return trimmed.startsWith('{') || trimmed.startsWith('[');
  };

  // Helper function to parse content (JSON or HTML) into EditorJS format
  const parseContentToEditorJS = (content) => {
    if (!content) return { blocks: [] };
    
    // Check if it's JSON (EditorJS format)
    if (isJsonContent(content)) {
      try {
        const parsed = JSON.parse(content);
        // Validate and sanitize blocks
        if (parsed && parsed.blocks && Array.isArray(parsed.blocks)) {
          parsed.blocks = parsed.blocks.map(block => {
            if (block.type === 'paragraph') {
              return {
                ...block,
                data: {
                  ...block.data,
                  text: typeof block.data?.text === 'string' ? block.data.text : ''
                }
              };
            }
            return block;
          }).filter(block => block && block.type);
          return parsed;
        }
        return { blocks: [] };
      } catch (e) {
        console.error('Error parsing JSON content:', e);
        return { blocks: [] };
      }
    }
    
    // It's HTML content - convert to EditorJS blocks
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    
    const blocks = [];
    
    // Parse an HTML <table> element into an EditorJS table block with style data
    const parseHtmlTable = (tableNode) => {
      const allRows = tableNode.querySelectorAll('tr');
      if (allRows.length === 0) return null;

      const thead = tableNode.querySelector('thead');
      const withHeadings = !!thead;

      const content = [];
      const cellBgColors = [];
      const colWidths = [];
      const rowHeights = [];

      allRows.forEach((tr) => {
        const cells = tr.querySelectorAll('td, th');
        const rowData = [];
        const rowColors = [];
        cells.forEach((cell, colIdx) => {
          rowData.push(cell.innerHTML.trim());
          const bgColor = cell.getAttribute('bgcolor') || cell.style.backgroundColor || '';
          rowColors.push(bgColor || null);

          // Capture column width from first row cells
          if (colWidths.length <= colIdx) {
            const w = cell.getAttribute('width') || cell.style.width || '';
            colWidths.push(w || null);
          }
        });
        content.push(rowData);
        cellBgColors.push(rowColors);

        // Capture row height from the row element itself
        const rowHeight = tr.getAttribute('height') || tr.style.height || tr.style.minHeight || '';
        rowHeights.push(rowHeight || null);
      });

      // Extract header background from thead th
      let headerBgColor = '';
      if (withHeadings) {
        const headerCell = thead.querySelector('th');
        if (headerCell) {
          headerBgColor = headerCell.getAttribute('bgcolor') || headerCell.style.backgroundColor || '';
        }
      }

      // Extract border styling from first cell
      let borderColor = '#ddd', borderWidth = '1px', borderStyle = 'solid';
      const firstCell = allRows[0].querySelector('td, th');
      if (firstCell) {
        const cellBorder = firstCell.style.border || '';
        if (cellBorder) {
          const parts = cellBorder.split(/\s+/);
          if (parts.length >= 3) {
            borderWidth = parts[0];
            borderStyle = parts[1];
            borderColor = parts.slice(2).join(' ');
          }
        }
      }

      return {
        type: 'table',
        data: {
          withHeadings,
          content,
          colWidths,
          rowHeights,
          headerBgColor,
          cellBgColors,
          borderColor,
          borderWidth,
          borderStyle,
        },
      };
    };

    // Recursive function to process nodes and extract visible content
    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (text) {
          blocks.push({ type: 'paragraph', data: { text } });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        
        // Skip non-visible elements
        if (['style', 'script', 'head', 'meta', 'link', 'title'].includes(tagName)) {
          return;
        }
        
        // Handle specific HTML elements
        if (tagName.match(/^h[1-6]$/)) {
          const text = node.innerHTML.trim();
          if (text) {
            const level = parseInt(tagName.charAt(1));
            const style = node.getAttribute('style') || '';
            blocks.push({ type: 'header', data: { text, level, style } });
          }
        } else if (tagName === 'p') {
          const text = node.innerHTML.trim();
          if (text) {
            const style = node.getAttribute('style') || '';
            blocks.push({ type: 'paragraph', data: { text, style } });
          }
        } else if (tagName === 'ul' || tagName === 'ol') {
          const items = [];
          const listItems = node.querySelectorAll('li');
          listItems.forEach(li => {
            const itemText = li.innerHTML.trim();
            if (itemText) items.push(itemText);
          });
          if (items.length > 0) {
            blocks.push({ 
              type: 'list', 
              data: { 
                style: tagName === 'ol' ? 'ordered' : 'unordered', 
                items 
              } 
            });
          }
        } else if (tagName === 'blockquote') {
          const text = node.innerHTML.trim();
          if (text) {
            blocks.push({ type: 'quote', data: { text, caption: '' } });
          }
        } else if (tagName === 'hr') {
          blocks.push({ type: 'delimiter', data: {} });
        } else if (tagName === 'img') {
          blocks.push({ 
            type: 'image', 
            data: { 
              file: { url: node.src }, 
              caption: node.alt || '' 
            } 
          });
        } else if (tagName === 'br') {
          // Skip br tags, they're handled within paragraphs
        } else if (tagName === 'table') {
          const tableBlock = parseHtmlTable(node);
          if (tableBlock) blocks.push(tableBlock);
        } else if (['div', 'section', 'article', 'main', 'center'].includes(tagName) && node.attributes.length > 0) {
          // Styled containers can't be represented as EditorJS blocks -
          // keep them verbatim in a raw HTML block so nothing is lost.
          blocks.push({ type: 'raw', data: { html: node.outerHTML } });
        } else if (['div', 'section', 'article', 'main', 'body', 'html', 'tbody', 'tr', 'td', 'th', 'thead', 'span', 'a', 'center'].includes(tagName)) {
          // For container elements, process their children
          const children = node.childNodes;
          for (let i = 0; i < children.length; i++) {
            processNode(children[i]);
          }
        } else {
          // For other elements, extract text content if meaningful
          const text = node.textContent.trim();
          if (text && text.length > 0) {
            blocks.push({ type: 'paragraph', data: { text } });
          }
        }
      }
    };
    
    // Process all top-level children
    const children = tempDiv.childNodes;
    for (let i = 0; i < children.length; i++) {
      processNode(children[i]);
    }
    
    // If no blocks were created, add the raw content as a paragraph (without style tags)
    if (blocks.length === 0 && content.trim()) {
      // Strip style and script tags from raw content
      const cleanContent = content.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
                                   .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
                                   .trim();
      if (cleanContent) {
        blocks.push({ type: 'paragraph', data: { text: cleanContent } });
      }
    }
    
    return { blocks };
  };

  useEffect(() => {
    // Initialize Editor.js only once
    if (!editorInstance.current && holderRef.current) {
      // Parse existing data (JSON or HTML) to Editor.js blocks format
      const initialData = parseContentToEditorJS(descriptionData);

      // Build tools configuration
      const tools = {
        // Alignment tune for blocks
        alignmentTune: {
          class: AlignmentTuneTool,
          config: {
            default: "left",
            blocks: {
              header: "left",
              paragraph: "left",
              quote: "left",
            }
          },
        },
        paragraph: {
          class: CustomParagraph,
          inlineToolbar: ["marker", "link", "bold", "italic", "inlineCode", "underline"],
          tunes: ["alignmentTune"],
        },
        header: {
          class: CustomHeader,
          inlineToolbar: ["marker", "link", "bold", "italic", "inlineCode", "underline"],
          config: {
            placeholder: "Enter a header",
            levels: [1, 2, 3, 4, 5, 6],
            defaultLevel: 2,
          },
          tunes: ["alignmentTune"],
        },
        list: {
          class: List,
          inlineToolbar: ["marker", "link", "bold", "italic", "inlineCode", "underline"],
          config: {
            defaultStyle: "unordered",
          },
        },
        table: {
          class: CustomTableTool,
          inlineToolbar: ["marker", "link", "bold", "italic", "inlineCode", "underline"],
          config: {
            rows: 2,
            cols: 3,
            withHeadings: false,
          },
        },
        image: {
          class: ResizableImageTool,
        },
        imageGallery: {
          class: ImageGalleryTool,
        },
        delimiter: {
          class: Delimiter,
        },
        raw: {
          class: RawHtmlTool,
        },
        quote: {
          class: Quote,
          inlineToolbar: true,
          config: {
            quotePlaceholder: "Enter a quote",
            captionPlaceholder: "Quote author",
          },
          tunes: ["alignmentTune"],
        },
        linkTool: {
          class: LinkTool,
          config: {
            endpoint: "/api/fetchUrl", // Your backend endpoint for url data fetching
          },
        },
        // Custom inline link tool that doesn't auto-add http:// for variables
        link: {
          class: CustomLinkInlineTool,
        },
        marker: {
          class: Marker,
          shortcut: "CMD+SHIFT+M",
        },
        inlineCode: {
          class: InlineCode,
          shortcut: "CMD+SHIFT+C",
        },
        underline: Underline,
      };

      // Add Color tool only if the plugin is properly loaded
      if (ColorPlugin) {
        tools.Color = {
          class: ColorPlugin
        };
        
        // Add Color to inline toolbars
        tools.paragraph.inlineToolbar.push("Color");
        tools.header.inlineToolbar.push("Color");
        tools.list.inlineToolbar.push("Color");
        tools.table.inlineToolbar.push("Color");
      }

      const inlineToolbar = ["marker", "link", "bold", "italic", "inlineCode", "underline"];
      if (ColorPlugin) {
        inlineToolbar.push("Color");
      }

      const editor = new EditorJS({
        holder: holderRef.current,
        readOnly: disabled,
        data: initialData,
        tools: tools,
        inlineToolbar: inlineToolbar,
        // Global sanitizer config to allow br tags
        sanitizer: {
          br: true,
          b: true,
          i: true,
          a: {
            href: true,
            target: '_blank',
            rel: 'noopener noreferrer'
          },
          mark: {
            class: true
          },
          code: true,
          u: true,
          font: {
            color: true,
            style: true
          }
        },
        onChange: async () => {
          // Skip if this change is from an external update (e.g., tab switch)
          if (isExternalUpdateRef.current) {
            return;
          }
          try {
            const savedData = await editorInstance.current.save();
            // Store as JSON string to preserve EditorJS format
            const jsonOutput = JSON.stringify(savedData);
            lastDescriptionDataRef.current = jsonOutput;
            setDescriptionData(jsonOutput);
          } catch (error) {
            console.error("Saving failed: ", error);
          }
        },
        placeholder: "Start typing your content here...",
      });

      // Wait for editor to be ready before storing reference
      editor.isReady
        .then(() => {
          editorInstance.current = editor;
          
          // Track selection changes within the editor
          const handleSelectionChange = () => {
            if (!isEditorFocusedRef.current) return;
            
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              // Only save if selection is within the editor
              if (holderRef.current && holderRef.current.contains(range.commonAncestorContainer)) {
                // Clone the range to preserve it
                lastRangeRef.current = range.cloneRange();
              }
            }
          };
          
          // Track focus state
          const handleFocusIn = (e) => {
            if (holderRef.current && holderRef.current.contains(e.target)) {
              isEditorFocusedRef.current = true;
            }
          };
          
          const handleFocusOut = (e) => {
            // Don't clear focus state immediately - allow time for variable click
            setTimeout(() => {
              if (holderRef.current && !holderRef.current.contains(document.activeElement)) {
                isEditorFocusedRef.current = false;
              }
            }, 100);
          };
          
          // Handle Tab key for indentation and Enter key in table cells
          const handleKeyDown = (e) => {
            // Handle Enter key in table cells - create new line instead of new row
            if (e.key === 'Enter' && !e.shiftKey) {
              const selection = window.getSelection();
              if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                // Check if we're inside a table cell
                const tableCell = range.startContainer.parentElement?.closest('.tc-cell') || 
                                  range.startContainer.closest?.('.tc-cell');
                if (tableCell) {
                  e.preventDefault();
                  e.stopImmediatePropagation();
                  
                  // Insert a <br> at the cursor position
                  range.deleteContents();
                  const br = document.createElement('br');
                  range.insertNode(br);
                  
                  // Move cursor after the <br>
                  range.setStartAfter(br);
                  range.collapse(true);
                  selection.removeAllRanges();
                  selection.addRange(range);
                  return;
                }
              }
            }
            
            if (e.key === 'Tab') {
              e.preventDefault();
              e.stopPropagation();
              
              const selection = window.getSelection();
              if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);
                
                // Check if we're inside an editable element
                const editableParent = range.startContainer.parentElement?.closest('[contenteditable="true"]');
                if (editableParent) {
                  // Delete any selected content first
                  range.deleteContents();
                  
                  // Insert tab character at cursor position
                  const tabNode = document.createTextNode('\t');
                  range.insertNode(tabNode);
                  
                  // Move cursor after the tab
                  range.setStartAfter(tabNode);
                  range.setEndAfter(tabNode);
                  selection.removeAllRanges();
                  selection.addRange(range);
                }
              }
            }
          };
          
          // Listen for selection changes
          document.addEventListener('selectionchange', handleSelectionChange);
          holderRef.current.addEventListener('focusin', handleFocusIn);
          holderRef.current.addEventListener('focusout', handleFocusOut);
          // Use capture phase (true) to intercept Enter key before table plugin handles it
          holderRef.current.addEventListener('keydown', handleKeyDown, true);
          
          // Store cleanup function
          const holderElement = holderRef.current;
          holderRef.current._cleanupSelection = () => {
            document.removeEventListener('selectionchange', handleSelectionChange);
            if (holderElement) {
              holderElement.removeEventListener('focusin', handleFocusIn);
              holderElement.removeEventListener('focusout', handleFocusOut);
              holderElement.removeEventListener('keydown', handleKeyDown, true);
            }
          };
        })
        .catch((error) => {
          console.error("Editor.js initialization failed:", error);
        });
    }

    // Cleanup
    return () => {
      if (holderRef.current && holderRef.current._cleanupSelection) {
        holderRef.current._cleanupSelection();
      }
      if (editorInstance.current && editorInstance.current.destroy) {
        editorInstance.current.destroy();
        editorInstance.current = null;
      }
    };
  }, []);

  // Update editor when disabled state changes
  useEffect(() => {
    if (editorInstance.current && editorInstance.current.readOnly) {
      editorInstance.current.readOnly.toggle(disabled);
    }
  }, [disabled]);

  // Update editor content when descriptionData changes externally (e.g., switching country tabs)
  useEffect(() => {
    // Only update if the content actually changed from an external source
    if (editorInstance.current && descriptionData !== lastDescriptionDataRef.current) {
      const updateEditor = async () => {
        try {
          // Set flag to prevent onChange from firing during this update
          isExternalUpdateRef.current = true;
          
          // Use the helper function to parse content (JSON or HTML)
          const newData = parseContentToEditorJS(descriptionData);
          
          await editorInstance.current.clear();
          await editorInstance.current.render(newData);
          lastDescriptionDataRef.current = descriptionData;
          
          // Reset flag after a short delay to allow EditorJS to finish
          setTimeout(() => {
            isExternalUpdateRef.current = false;
          }, 100);
        } catch (error) {
          console.error('Error updating editor content:', error);
          isExternalUpdateRef.current = false;
        }
      };
      
      updateEditor();
    }
  }, [descriptionData]);

  // Convert Editor.js blocks to HTML
  const convertEditorJSToHTML = (data) => {
    if (!data || !data.blocks) return "";

    // Helper to get alignment style
    const getAlignmentStyle = (block) => {
      const alignment = block.tunes?.alignmentTune?.alignment || 'left';
      return alignment !== 'left' ? `text-align: ${alignment}` : '';
    };

    return data.blocks
      .map((block) => {
        const alignStyle = getAlignmentStyle(block);
        
        switch (block.type) {
          case "header": {
            const mergedStyle = [block.data.style, alignStyle].filter(Boolean).join('; ').trim();
            const styleAttr = mergedStyle ? ` style="${mergedStyle}"` : '';
            return `<h${block.data.level}${styleAttr}>${block.data.text}</h${block.data.level}>`;
          }
          case "paragraph": {
            const mergedStyle = [block.data.style, alignStyle].filter(Boolean).join('; ').trim();
            const styleAttr = mergedStyle ? ` style="${mergedStyle}"` : '';
            return `<p${styleAttr}>${block.data.text}</p>`;
          }
          case "list":
            const tag = block.data.style === "ordered" ? "ol" : "ul";
            const items = block.data.items
              .map((item) => {
                // Handle both string items and object items (newer EditorJS versions)
                const content = typeof item === 'string' ? item : (item.content || item.text || '');
                return `<li>${content}</li>`;
              })
              .join("");
            return `<${tag}>${items}</${tag}>`;
          case "table": {
            const withHeadings = !!block.data.withHeadings;
            const content = block.data.content || [];
            const bodyRows = withHeadings ? content.slice(1) : content;
            const headerRow = withHeadings ? content[0] : null;
            const colWidths = block.data.colWidths || [];
            const rowHeights = block.data.rowHeights || [];
            const headerBgColor = block.data.headerBgColor || "";
            const cellBgColors = block.data.cellBgColors || [];
            const borderColor = block.data.borderColor || "#ddd";
            const borderWidth = block.data.borderWidth || "1px";
            const borderStyle = block.data.borderStyle || "solid";
            const borderCss = `${borderWidth} ${borderStyle} ${borderColor}`;
            const tableStyle = `border-collapse: collapse; width: 100%;`;

            const headHtml = headerRow
              ? `<thead><tr${rowHeights[0] ? ` height="${rowHeights[0]}"` : ""}>${headerRow
                  .map((cell, i) => {
                    const w = colWidths[i] ? ` width="${colWidths[i]}"` : "";
                    const cellBg = cellBgColors[0]?.[i];
                    let bg;
                    if (cellBg) {
                      bg = ` style="border: ${borderWidth} ${borderStyle} ${cellBg}; background-color: ${cellBg};"`;
                    } else if (headerBgColor) {
                      bg = ` style="border: ${borderCss}; background-color: ${headerBgColor}; color: #fff; font-weight: bold;"`;
                    } else {
                      bg = ` style="border: ${borderCss};"`;
                    }
                    return `<th${w}${bg}>${cell}</th>`;
                  })
                  .join("")}</tr></thead>`
              : "";
            const bodyHtml = bodyRows
              .map(
                (row, rIdx) => {
                  const dataRowIdx = withHeadings ? rIdx + 1 : rIdx;
                  const h = rowHeights[dataRowIdx] ? ` height="${rowHeights[dataRowIdx]}"` : "";
                  return `<tr${h}>${row.map((cell, cIdx) => {
                    const w = colWidths[cIdx] ? ` width="${colWidths[cIdx]}"` : "";
                    const cellBg = cellBgColors[dataRowIdx]?.[cIdx];
                    const bgStyle = cellBg ? `background-color: ${cellBg}; ` : "";
                    return `<td${w} style="${bgStyle}border: ${borderCss};">${cell}</td>`;
                  }).join("")}</tr>`;
                }
              )
              .join("");
            return `<table style="${tableStyle}">${headHtml}<tbody>${bodyHtml}</tbody></table>`;
          }
          case "linkTool":
            return `<a href="${block.data.link}" target="_blank">${block.data.meta?.title || block.data.link}</a>`;
          case "image":
            const imgCaption = block.data.caption ? `<figcaption style="text-align: center; font-size: 14px; color: #666; margin-top: 8px;">${block.data.caption}</figcaption>` : '';
            // Support both old format (file.url) and new format (url directly)
            const imgUrl = block.data.url || block.data.file?.url || '';
            const imgWidth = block.data.width || '100%';
            const imgAlignment = block.data.alignment || 'center';
            return `<figure style="margin: 10px 0; text-align: ${imgAlignment};"><img src="${imgUrl}" alt="${block.data.caption || ''}" style="width: ${imgWidth}; max-width: 100%; height: auto;" />${imgCaption}</figure>`;
          case "imageGallery":
            const galleryImages = block.data.images || [];
            const galleryLayout = block.data.layout === 'auto' 
              ? Math.min(galleryImages.length || 1, 4) 
              : parseInt(block.data.layout);
            const galleryGap = block.data.gap || '10px';
            const galleryImagesHtml = galleryImages.map(img => {
              const caption = img.caption ? `<figcaption style="text-align: center; font-size: 12px; color: #666; padding: 5px;">${img.caption}</figcaption>` : '';
              return `<figure style="margin: 0;"><img src="${img.url}" alt="${img.caption || ''}" style="width: 100%; height: auto; display: block;" />${caption}</figure>`;
            }).join('');
            return `<div style="display: grid; grid-template-columns: repeat(${galleryLayout}, 1fr); gap: ${galleryGap}; margin: 10px 0;">${galleryImagesHtml}</div>`;
          case "delimiter":
            return `<hr />`;
          case "raw":
            return block.data.html || "";
          case "quote":
            const quoteCaption = block.data.caption ? `<cite>${block.data.caption}</cite>` : '';
            const quoteStyleAttr = alignStyle ? ` style="${alignStyle};"` : '';
            return `<blockquote${quoteStyleAttr}><p>${block.data.text}</p>${quoteCaption}</blockquote>`;
          default:
            return `<p>${block.data?.text || ""}</p>`;
        }
      })
      .join("");
  };

  return (
    <div
      ref={holderRef}
      id="editorjs"
      style={{
        border: "1px solid #ccc",
        borderRadius: "4px",
        minHeight: "300px",
        padding: "10px",
      }}
    />
  );
});

EditorJSText.propTypes = {
  t: PropTypes.func.isRequired,
  setDescriptionData: PropTypes.func.isRequired,
  descriptionData: PropTypes.string,
  disabled: PropTypes.bool,
};

EditorJSText.defaultProps = {
  descriptionData: "",
  disabled: false,
};

EditorJSText.displayName = 'EditorJSText';

export default withTranslation()(EditorJSText);
